import { useEffect, useRef, useState } from 'react';
import type { DemoScript, Fx, Level, Pt, RoundProps, ViewProps } from '../kit.js';
import { BAD, BOARD, BoardFrame, OK, STROKE, TYPE, clamp, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';
import { ThingSvg, hasShadowVariant, mix, paperOf, pick, pop, shuffle, thingDef } from './things.js';
import type { ThingId } from './things.js';

/**
 * ARCHIVIST · MATCH THE SHADOW — pick the outline that fits the object.
 *
 * A museum piece (or a piece of wreckage) stands in a pool of light; below it, three shadows. Tap
 * the one that fits: it lights up as the object, and the next object comes. A wrong one is a
 * mistake; it fades and takes a cross. Three objects a round. In rounds 1 and 2 the other shadows
 * are other things; in round 3 one of them is the object turned round (its mirror image), so it
 * matters which way the object faces.
 *
 * On the kit (kit.tsx): the plinth and the shadows are drawn in the board's own 320 × 320 space;
 * the view state (`ShadowView`) names the object and its shadows and says which were tried.
 */

/** One outline: which thing, flipped or not, with its one different part or not. */
export interface Outline {
  thing: ThingId;
  mirror: boolean;
  variant: boolean;
}

export interface ShadowTurn {
  target: Outline;
  options: Outline[];
  answer: number;
}

/** Things that face one way and have a part that changes their outline: every one plays round 3. */
export const SHADOW_POOL: Record<Era, readonly ThingId[]> = {
  stars: ['teapot', 'telescope', 'key', 'lamp', 'umbrella', 'boot', 'can', 'mug', 'radio'],
  neon: ['radio', 'lamp', 'helmet', 'umbrella', 'mug', 'teapot', 'key', 'telescope'],
  debris: ['helmet', 'boot', 'wrench', 'radio', 'lamp', 'telescope', 'key', 'mug', 'can'],
  dust: ['wrench', 'key', 'boot', 'can', 'lamp', 'telescope', 'helmet', 'radio'],
  canopy: ['can', 'teapot', 'umbrella', 'bird', 'fish', 'boat', 'house', 'mug'],
};

const TURNS = 3;
const same = (a: Outline, b: Outline) => a.thing === b.thing && a.mirror === b.mirror && a.variant === b.variant;

/** One object and the outlines to choose from, at a level. */
export function shadowTurn(level: Level, thing: ThingId, others: readonly ThingId[], rand: () => number): ShadowTurn {
  const target: Outline = { thing, mirror: rand() < 0.5, variant: rand() < 0.5 };
  const other = (t: ThingId): Outline => ({ thing: t, mirror: rand() < 0.5, variant: rand() < 0.5 });
  // Easier since 6 Oct 2026: rounds 1 and 2 offer two other things, round 3 the object turned
  // round and one other thing (the old round 2). The old round 3, all three the object, is gone.
  const wrong: Outline[] = level < 2 ? others.slice(0, 2).map(other) : [{ ...target, mirror: !target.mirror }, other(others[0]!)];
  const options = shuffle([target, ...wrong], rand);
  return { target, options, answer: options.findIndex((o) => same(o, target)) };
}

/** A round's three objects, all different, and none of those in `avoid` while there are others. */
export function shadowTurns(level: Level, era: Era, rand: () => number, avoid: readonly ThingId[] = []): ShadowTurn[] {
  const pool = SHADOW_POOL[era];
  const fresh = pool.filter((t) => !avoid.includes(t));
  const things = pick(fresh.length >= TURNS ? fresh : pool, TURNS, rand);
  return things.map((thing) => {
    const others = pick(
      pool.filter((t) => t !== thing),
      2,
      rand,
    );
    return shadowTurn(level, thing, others, rand);
  });
}

/** Every outline differs from the others, and exactly one is the object's. */
export function fairTurn(turn: ShadowTurn): boolean {
  const { options, target } = turn;
  const distinct = options.every((o, i) => options.every((p, j) => i === j || !same(o, p)));
  const shapes = options.every((o) => thingDef(o.thing).asym || !o.mirror) && options.every((o) => hasShadowVariant(o.thing) || !o.variant);
  return distinct && shapes && options.filter((o) => same(o, target)).length === 1 && same(options[turn.answer]!, target);
}

/** After the right shadow, the next object comes this much later. */
const NEXT_MS = 800;
/** A new object comes onto the plinth over this. */
const POP_MS = 320;
/** The right shadow lights up as the object over this. */
const LIGHT_MS = 350;
/** A wrong shadow fades over this. */
const FADE_MS = 300;

/** The objects shown in the last rounds, so the next round shows others. */
const recent: ThingId[] = [];

/** The object, standing in its light (board units). */
const OBJECT = { size: 124, top: 16 } as const;
/** The plinth's top. */
const PLINTH_Y = 152;
/** The shadows: a row along the bottom. */
const ROW = { x: 10, y: 186, h: 124, gap: 8 } as const;

/** Where shadow `i` of `n` lies, in board units. */
export function optionBox(i: number, n: number): { x: number; y: number; w: number; h: number } {
  const w = (BOARD.w - 2 * ROW.x - (n - 1) * ROW.gap) / n;
  return { x: ROW.x + i * (w + ROW.gap), y: ROW.y, w, h: ROW.h };
}

/** The shadow under a finger at `p`, or −1 (a touch in the gap goes to the nearer one). */
export function optionAt(p: Pt, n: number): number {
  if (p.y < ROW.y - ROW.gap || p.y > ROW.y + ROW.h + ROW.gap) return -1;
  for (let i = 0; i < n; i++) {
    const b = optionBox(i, n);
    if (p.x >= b.x - ROW.gap / 2 && p.x <= b.x + b.w + ROW.gap / 2) return i;
  }
  return -1;
}

/** An outline in a view state: the thing, mirrored (1) or not, its one part changed (1) or not. */
export type Shape = readonly [thing: ThingId, mirror: number, variant: number];

const shapeOf = (o: Outline): Shape => [o.thing, o.mirror ? 1 : 0, o.variant ? 1 : 0];

/** Everything the plinth and the shadows show. */
export interface ShadowView {
  /** The game clock, ms. */
  t: number;
  /** The object on the plinth. */
  o: Shape;
  /** The shadows to choose from. */
  c: readonly Shape[];
  /** Each shadow tried and wrong, 0–1 (it fades and takes a cross). */
  x: readonly number[];
  /** The shadow found (−1 while none), and how far it has lit up as the object, 0–1. */
  r: number;
  l: number;
  /** Objects matched this round, and how many there are. */
  n: number;
  of: number;
  /** 0 choosing, 1 the last try was wrong, 2 found, 3 every object matched. */
  s: 0 | 1 | 2 | 3;
  /** The object coming onto the plinth, 0–1. */
  p: number;
  fx?: Fx | undefined;
}

/** The pool of light the object stands in, on a small plinth (or on rubble, among the wreckage). */
function Plinth({ era, id }: { era: Era; id: string }) {
  const skin = ERA_SKIN[era];
  const wreck = era === 'debris' || era === 'dust';
  const c = BOARD.w / 2;
  return (
    <g>
      <path d={`M${c - 40} 0H${c + 40}L${c + 95} ${PLINTH_Y + 12}H${c - 95}Z`} fill={`url(#${id}light)`} />
      {wreck ? (
        <>
          <path d={`M${c - 70} ${PLINTH_Y + 12}L${c - 48} ${PLINTH_Y}L${c - 18} ${PLINTH_Y + 6}L${c + 10} ${PLINTH_Y - 4}L${c + 46} ${PLINTH_Y + 4}L${c + 70} ${PLINTH_Y + 12}Z`} fill={mix(skin.ink, '#000000', 0.55)} />
          <path d={`M${c - 64} ${PLINTH_Y + 10}L${c - 42} ${PLINTH_Y + 2}L${c - 18} ${PLINTH_Y + 8}L${c + 10} ${PLINTH_Y}L${c + 40} ${PLINTH_Y + 6}`} stroke={skin.haze} strokeOpacity=".35" strokeWidth="1.2" fill="none" />
        </>
      ) : (
        <>
          <ellipse cx={c} cy={PLINTH_Y} rx="58" ry="7" fill="#000000" opacity=".45" />
          <path d={`M${c - 56} ${PLINTH_Y}H${c + 56}L${c + 50} ${PLINTH_Y + 20}H${c - 50}Z`} fill={`url(#${id}top)`} />
          <path d={`M${c - 56} ${PLINTH_Y}H${c + 56}`} stroke={skin.haze} strokeOpacity=".6" strokeWidth="1.4" />
        </>
      )}
    </g>
  );
}

/** The plinth and the shadows, from the view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<ShadowView>) {
  const id = useSvgId();
  const skin = ERA_SKIN[era];
  const paper = paperOf(era);
  const [thing, mirror, variant] = view.o;
  const found = Math.round(view.r);
  const done = Math.round(view.n);
  const status =
    view.s === 3
      ? { text: say(lang, '✓ ALL MATCHED', '✓ ALLE FUNNET'), tone: 'ok' as const }
      : view.s === 2
        ? { text: say(lang, '✓ THAT ONE', '✓ DEN'), count: [done, view.of] as const, tone: 'ok' as const }
        : view.s === 1
          ? { text: say(lang, 'NOT THAT ONE', 'IKKE DEN'), tone: 'bad' as const }
          : { text: say(lang, 'SHADOWS', 'SKYGGER'), count: [done, view.of] as const, hint: say(lang, 'WHICH ONE FITS?', 'HVILKEN PASSER?') };
  // The object comes on with a small pop, then floats gently in its light.
  const come = pop(view.p);
  const bob = 1.25 * Math.sin((view.t / 3_200) * 2 * Math.PI) + 0.25;
  const cx = BOARD.w / 2;
  const oy = OBJECT.top + OBJECT.size / 2;

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <radialGradient id={`${id}light`} cx=".5" cy="0" r="1">
          <stop offset="0" stopColor={skin.haze} stopOpacity=".32" />
          <stop offset=".7" stopColor={skin.haze} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}top`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={mix(skin.ink, '#ffffff', 0.35)} />
          <stop offset="1" stopColor={mix(skin.ink, '#000000', 0.55)} />
        </linearGradient>
        <linearGradient id={`${id}paper`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={paper.top} />
          <stop offset="1" stopColor={paper.bottom} />
        </linearGradient>
      </defs>
      <Plinth era={era} id={id} />
      <g transform={`translate(${cx} ${q(oy + bob, 2)}) scale(${q(come.scale, 3)})`} opacity={q(come.opacity, 2)}>
        <ThingSvg thing={thing} mirror={mirror > 0.5} variant={variant > 0.5} x={-OBJECT.size / 2} y={-OBJECT.size / 2} width={OBJECT.size} height={OBJECT.size} />
      </g>

      {view.c.map(([t, m, v], i) => {
        const b = optionBox(i, view.c.length);
        const right = found === i;
        const wrong = clamp(view.x[i] ?? 0, 0, 1);
        const lit = right ? clamp(view.l, 0, 1) : 0;
        const pad = 6;
        const size = Math.min(b.w, b.h) - 2 * pad;
        return (
          <g key={i} opacity={q(1 - 0.6 * wrong, 2)}>
            {right && <rect x={b.x - 3} y={b.y - 3} width={b.w + 6} height={b.h + 6} rx="13" fill="none" stroke={OK} strokeWidth="6" opacity={0.4 * lit} />}
            <rect x={b.x + 1} y={b.y + 4} width={b.w} height={b.h} rx="10" fill="#000000" opacity=".4" />
            <rect x={b.x} y={b.y} width={b.w} height={b.h} rx="10" fill={`url(#${id}paper)`} stroke={right ? OK : wrong > 0.5 ? BAD : paper.edge} strokeWidth={STROKE.line} />
            <ThingSvg thing={t} mirror={m > 0.5} variant={v > 0.5} mode="shadow" ink={skin.bg} x={b.x + b.w / 2 - size / 2} y={b.y + b.h / 2 - size / 2} width={size} height={size} />
            {lit > 0 && (
              <g opacity={q(lit, 2)}>
                <ThingSvg thing={t} mirror={m > 0.5} variant={v > 0.5} x={b.x + b.w / 2 - size / 2} y={b.y + b.h / 2 - size / 2} width={size} height={size} />
              </g>
            )}
            {wrong > 0.5 && (
              <text x={b.x + b.w - 12} y={b.y + 20} textAnchor="middle" fontFamily={mono} fontSize={TYPE.button} fontWeight="700" fill={BAD}>
                ✕
              </text>
            )}
          </g>
        );
      })}
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, era, lang } = props;
  const [turns] = useState(() => shadowTurns(level, era, seeded(Date.now() + level * 7919), recent.slice(0, 5)));
  useEffect(() => {
    for (const { target } of turns) {
      if (recent.includes(target.thing)) recent.splice(recent.indexOf(target.thing), 1);
      recent.unshift(target.thing);
    }
    recent.length = Math.min(recent.length, 6);
  }, [turns]);
  const at = useRef(0);
  /** When this object came onto the plinth (game clock). */
  const cameAt = useRef<number | null>(null);
  /** The shadows tried and wrong, and when. */
  const wrong = useRef(new Map<number, number>());
  const lastWrong = useRef(false);
  /** When the right shadow was found, and when the next object comes. */
  const foundAt = useRef<number | null>(null);
  const due = useRef<number | null>(null);

  const round = useRound(props, (_dt, now) => {
    if (cameAt.current === null) cameAt.current = now;
    if (due.current === null || now < due.current) return;
    due.current = null;
    at.current += 1;
    cameAt.current = now;
    foundAt.current = null;
    wrong.current = new Map();
    lastWrong.current = false;
  });

  const choose = (p: Pt) => {
    if (!round.live || foundAt.current !== null) return;
    const turn = turns[at.current]!;
    const i = optionAt(p, turn.options.length);
    if (i < 0 || wrong.current.has(i)) return;
    const now = round.time();
    if (i === turn.answer) {
      foundAt.current = now;
      lastWrong.current = false;
      round.hit();
      round.progress((at.current + 1) / turns.length);
      if (at.current + 1 >= turns.length) round.win();
      else due.current = now + NEXT_MS;
      return;
    }
    wrong.current.set(i, now);
    lastWrong.current = true;
    round.miss();
  };

  const now = round.now;
  const turn = turns[at.current]!;
  const view: ShadowView = {
    t: q(now),
    o: shapeOf(turn.target),
    c: turn.options.map(shapeOf),
    x: turn.options.map((_, i) => {
      const when = wrong.current.get(i);
      return when === undefined ? 0 : q(clamp((now - when) / FADE_MS, 0, 1), 2);
    }),
    r: foundAt.current === null ? -1 : turn.answer,
    l: foundAt.current === null ? 0 : q(clamp((now - foundAt.current) / LIGHT_MS, 0, 1), 2),
    n: at.current + (foundAt.current === null ? 0 : 1),
    of: turns.length,
    s: round.won ? 3 : foundAt.current !== null ? 2 : lastWrong.current ? 1 : 0,
    p: cameAt.current === null ? 0 : q(clamp((now - cameAt.current) / POP_MS, 0, 1), 2),
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Tap the shadow that fits', 'Trykk på skyggen som passer'), down: choose }}
    />
  );
}

const mid = (i: number) => {
  const b = optionBox(i, 3);
  return { x: q(b.x + b.w / 2), y: q(b.y + b.h / 2) };
};
const DEMO_A = mid(1);
const DEMO_B = mid(0);

/**
 * The how-to, on round 1: a teapot on the plinth; the finger taps its shadow among a key's and a
 * boot's, and it lights up as the teapot. Then a lamp comes, and the finger taps the lamp's.
 */
const demo: DemoScript<ShadowView> = {
  ms: 6_800,
  keys: [
    {
      at: 0,
      view: {
        t: 0,
        o: ['teapot', 0, 0],
        c: [['key', 0, 0], ['teapot', 0, 0], ['boot', 1, 0]],
        x: [0, 0, 0],
        r: -1,
        l: 0,
        n: 0,
        of: 3,
        s: 0,
        p: 1,
      },
    },
    { at: 1_800, view: { r: 1, n: 1, s: 2, fx: [1, 0] }, ease: 'step' },
    { at: 1_800 + LIGHT_MS, view: { l: 1 } },
    { at: 2_400, view: { fx: [0, 0] } },
    {
      at: 2_900,
      view: { o: ['lamp', 1, 0], c: [['lamp', 1, 0], ['umbrella', 0, 0], ['mug', 1, 0]], r: -1, l: 0, s: 0, p: 0 },
      ease: 'step',
    },
    { at: 2_900 + POP_MS, view: { p: 1 } },
    { at: 4_300, view: { r: 0, n: 2, s: 2, fx: [1, 0] }, ease: 'step' },
    { at: 4_300 + LIGHT_MS, view: { l: 1 } },
    { at: 4_900, view: { fx: [0, 0] } },
  ],
  finger: [
    { at: 0, x: 280, y: 300, hide: true },
    { at: 600, x: 280, y: 300, hide: false },
    { at: 1_500, x: DEMO_A.x, y: DEMO_A.y },
    { at: 1_800, x: DEMO_A.x, y: DEMO_A.y, down: true },
    { at: 1_950, x: DEMO_A.x, y: DEMO_A.y, down: false },
    { at: 3_900, x: DEMO_B.x, y: DEMO_B.y },
    { at: 4_300, x: DEMO_B.x, y: DEMO_B.y, down: true },
    { at: 4_450, x: DEMO_B.x, y: DEMO_B.y, down: false },
    { at: 5_200, x: 280, y: 300 },
    { at: 5_400, x: 280, y: 300, hide: true },
  ],
  steps: [
    { at: 0, en: 'LOOK AT THE THING', no: 'SE PÅ TINGEN' },
    { at: 1_000, en: 'TAP THE SHADOW THAT FITS', no: 'TRYKK PÅ SKYGGEN SOM PASSER' },
    { at: 2_900, en: 'THREE THINGS A ROUND', no: 'TRE TING HVER RUNDE' },
  ],
};

export const game = thirtyGame<ShadowView>({
  Board,
  View,
  demo,
  words: {
    en: {
      instruction: 'Tap the shadow that fits the object exactly. Watch which way it faces.',
      howMove: 'Look at the object, then tap the one shadow with exactly its shape.',
    },
    no: {
      instruction: 'Trykk på skyggen som passer nøyaktig. Se hvilken vei den vender.',
      howMove: 'Se på tingen, og trykk på den ene skyggen med nøyaktig samme form.',
    },
  },
});
